import React from "react"
import {
  Box,
  VStack,
  Text,
  Button,
  Heading,
  Progress,
  HStack,
  Link,
} from "@chakra-ui/react"
import { Api } from "@/lib/api"
import Papa from "papaparse"
import { addAlert, AlertType, removeAlertBySource } from "@/lib/alerts"
import {
  JobStatus,
  FileName,
  FileError,
  UploadProgress,
  IsUploading,
  UploadStatus,
  CurrentOrg,
} from "@/lib/store"
import { useParams } from "react-router-dom"
import useFileStore from "../lib/fileStore"
import { getPlanLimits, CONTACT_SALES_URL } from "@/lib/limits"

export default function UploadRecords() {
  const { schemaId } = useParams()
  const fileInputRef = React.useRef(null)
  const api = new Api()
  const [jobId, setJobId] = React.useState(null)
  const [currentOrg] = CurrentOrg.useState()

  const [jobStatus, setJobStatus] = JobStatus.useState()
  const [fileName, setFileName] = FileName.useState()
  const [fileError, setFileError] = FileError.useState()
  const [uploadProgress, setUploadProgress] = UploadProgress.useState()
  const [isUploading, setIsUploading] = IsUploading.useState()

  const { selectedFile, setSelectedFile, clearSelectedFile } = useFileStore()

  React.useEffect(() => {
    return () => {
      setIsUploading(UploadStatus.NONE)
      setUploadProgress(0)
      setJobStatus(null)
      setFileName("")
      setFileError(null)
      if (selectedFile) {
        setSelectedFile(null)
      }
    }
  }, [])

  React.useEffect(() => {
    if (jobId === null) return
    api.jobsWebsocket(jobId)
  }, [jobId])

  function openAttachment() {
    fileInputRef.current.click()
  }

  const handleFileDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    const fileTypes = ["application/json", "text/csv"]
    if (fileTypes.includes(file.type)) {
      removeAlertBySource("File Upload")
      setSelectedFile(file)
      setFileName(file.name)
      setUploadProgress(0)
      setIsUploading(UploadStatus.NONE)
      setJobStatus(null)
    } else {
      addAlert(AlertType.ERROR, "File Upload", "Invalid file type")
      console.log("File error on handleFileDrop:", fileError)
    }
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      setSelectedFile(file)
      setFileName(file.name)
      removeAlertBySource("File Upload")
      setUploadProgress(0)
      setIsUploading(UploadStatus.NONE)
      setJobStatus(null)
    }
  }

  const uploadData = async () => {
    try {
      setIsUploading(UploadStatus.UPLOADING)
      setFileName(selectedFile.name)
      removeAlertBySource("File Upload")
      let [file, fileType] = await parseFile(selectedFile).catch((error) => {
        setFileError(error.message)
        setIsUploading(UploadStatus.TERMINATED)
        setUploadProgress(0)
        return [null, null]
      })

      if (!file || !fileType) {
        return
      }

      const response = await api.jobsUpload(file, fileType)
      console.log("Job ID:", response.job_id)
      setJobId(response.job_id)
    } catch (error) {
      console.error("Error uploading data:", error)
      setIsUploading(UploadStatus.TERMINATED)
      setUploadProgress(0)
    }
  }

  //TODO(Alexandrite): handle buffered file upload for supermassive files
  const parseFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (event) => {
        try {
          const fileContent = event.target.result

          if (file.type === "text/csv") {
            resolve([handleCSV(fileContent), file.type])
          } else {
            resolve([handleJSON(fileContent), file.type])
          }
        } catch (error) {
          setIsUploading(UploadStatus.TERMINATED)
          reject(new Error(`File parsing failed: ${error.message}`))
        }
      }

      const handleCSV = (fileContent) => {
        let csv = Papa.parse(fileContent, {
          header: false, // We need to handle the header manually.
          skipEmptyLines: true,
          transformHeader: (header) => header.trim(),
          transform: (value) => value.trim(),
        })

        console.log("csv=", csv)

        if (csv.errors.length > 0) {
          throw new Error("CSV parsing errors", csv.errors)
        }

        const rows = csv.data
        let header = rows[0]

        // If the header is the same length as the rows, we need to add a schema header.
        if (header.length == rows[1].length) {
          const schemaHeader = `schema_id:${schemaId}`
          header.push(schemaHeader)
        }

        const recordCount = rows.length - 1
        const { maxRecordsPerSchema } = getPlanLimits(currentOrg)
        if (maxRecordsPerSchema !== null && recordCount > maxRecordsPerSchema) {
          const errorMessage = `Upload failed: File contains ${recordCount.toLocaleString()} records but your plan supports up to ${maxRecordsPerSchema.toLocaleString()} per schema. Upgrade at ${CONTACT_SALES_URL}.`
          addAlert(AlertType.ERROR, "File Upload", errorMessage)
          throw new Error(errorMessage)
        }
        return Papa.unparse(rows)
      }

      const handleJSON = (fileContent) => {
        let json
        try {
          json = JSON.parse(fileContent)
        } catch (error) {
          if (fileContent.trim() === "") {
            addAlert(
              AlertType.ERROR,
              "File Upload",
              "File is empty, please select a different file.",
            )
            throw new Error()
          }
          addAlert(
            AlertType.ERROR,
            "File Upload",
            "Invalid JSON object, please check file format or select a different file.",
          )
          throw new Error()
        }

        // Convert to array if single object
        const records = Array.isArray(json) ? json : [json]

        const { maxRecordsPerSchema } = getPlanLimits(currentOrg)
        if (
          maxRecordsPerSchema !== null &&
          records.length > maxRecordsPerSchema
        ) {
          const errorMessage = `Upload failed: File contains ${records.length.toLocaleString()} records but your plan supports up to ${maxRecordsPerSchema.toLocaleString()} per schema. Upgrade at ${CONTACT_SALES_URL}.`
          addAlert(AlertType.ERROR, "File Upload", errorMessage)
          throw new Error(errorMessage)
        }

        // If the schemaId is set (which it should be), add the schema URL to each record.
        if (schemaId) {
          const schemaUrl = `/api/schemas/${schemaId}/`
          // If the record already has a schema, skip it.
          for (let record of records) {
            if (!record?.schema) {
              record.schema = schemaUrl
            }
          }
        }

        return JSON.stringify(records)
      }

      reader.onerror = () => {
        setIsUploading(UploadStatus.TERMINATED)
        addAlert(AlertType.ERROR, "File Upload", "Failed to read file.", 4)
        reject(new Error("Failed to read file"))
      }

      reader.readAsText(file)
    })
  }

  const clearFile = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }

    // Clear states
    setIsUploading(UploadStatus.NONE)
    setUploadProgress(0)
    setJobStatus(null)
    if (!jobStatus) {
      setFileName("")
    }
    removeAlertBySource("File Upload")
    removeAlertBySource("Api Error")
    clearSelectedFile()
  }

  return (
    <Box bg="white" borderRadius="md" p="2">
      <Box
        display="flex"
        justifyContent={"center"}
        alignItems={"center"}
        height="auto"
        aspectRatio={{ base: "none", md: "1" }}
      >
        {isUploading !== UploadStatus.NONE ? (
          <Box p={8}>
            {isUploading === UploadStatus.UPLOADING ? (
              <VStack
                justifyContent={"center"}
                alignItems={"center"}
                maxW="sm"
                w="100%"
                p={4}
              >
                <Heading variant="standardMicro" mt={8}>
                  Uploading
                </Heading>
                <Text variant="base" textAlign="center" w="100%">
                  Uploading records from file:
                  <br />
                  {fileName}
                </Text>
                <Text variant="base">
                  {jobStatus?.status}: {jobStatus?.processed} of{" "}
                  {jobStatus?.total} records
                </Text>
                <Text variant="base">{uploadProgress}%</Text>
                <Progress w="100%" min={0} max={100} value={uploadProgress} />
              </VStack>
            ) : (
              <VStack justifyContent={"center"} alignItems={"center"}>
                <Heading variant="standardMicro" mt={8}>
                  {isUploading === UploadStatus.TERMINATED
                    ? "Upload Terminated"
                    : "Upload Complete"}
                </Heading>
                <Text variant="base">
                  {isUploading === UploadStatus.TERMINATED
                    ? `Failed to upload records from file: ${fileName}`
                    : `Uploaded ${jobStatus?.total} records from file: ${fileName}`}
                </Text>
                <Text variant="base">{jobStatus?.status}</Text>
                {isUploading === UploadStatus.TERMINATED && (
                  <Text variant="base">{uploadProgress}%</Text>
                )}
                <Progress width="100%" value={uploadProgress} />
              </VStack>
            )}
          </Box>
        ) : (
          <VStack
            display="flex"
            height="80%"
            width={{ base: "100%", md: "80%" }}
            margin="auto"
            borderRadius="md"
            bg="Gray.100"
            textAlign="center"
            justifyContent={"center"}
            alignItems={"center"}
            border="5px dotted"
            borderColor="Gray.200"
            pt={{ base: "0", md: "auto" }}
            p={{ base: "2" }}
            _hover={{
              bg: "Gray.200",
              borderColor: "Gray.400",
            }}
            onDrop={handleFileDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <Text variant="base">Drag and drop your data here</Text>
            <Text variant="base">or</Text>
            <HStack>
              <Button
                type="file"
                variant="primary"
                size={{ base: "sm", lg: "md" }}
                onClick={openAttachment}
              >
                {selectedFile ? fileName : "Select File"}
              </Button>
              {selectedFile && (
                <Button
                  id="clear-file-button"
                  variant="primary"
                  size={{ base: "sm", lg: "md" }}
                  onClick={clearFile}
                >
                  ✕
                </Button>
              )}
            </HStack>
            <input
              type="file"
              accept=".json,.csv"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </VStack>
        )}
      </Box>
      {(() => {
        const { maxRecordsPerSchema } = getPlanLimits(currentOrg)
        return (
          maxRecordsPerSchema !== null && (
            <Box px={4} pt={2} pb={0}>
              <Text
                variant="base"
                fontSize="sm"
                color="Gray.600"
                textAlign="center"
              >
                Your plan supports up to {maxRecordsPerSchema.toLocaleString()}{" "}
                records per schema.{" "}
                <Link
                  href={CONTACT_SALES_URL}
                  isExternal
                  textDecoration="underline"
                >
                  Upgrade your plan
                </Link>{" "}
                for more.
              </Text>
            </Box>
          )
        )
      })()}
      <HStack
        spacing={4}
        m={4}
        pt={0}
        mt={0}
        mr={{ base: 0, md: 4 }}
        justify="space-between"
      >
        <VStack spacing={2} width="fit-content"></VStack>
        <Button
          mt={{ base: 4, md: 0 }}
          variant="primary"
          size={{ base: "sm", lg: "md" }}
          onClick={uploadData}
          isDisabled={isUploading !== UploadStatus.NONE || !selectedFile}
        >
          {isUploading === UploadStatus.UPLOADING ? "Uploading..." : "Upload"}
        </Button>
      </HStack>
    </Box>
  )
}
